/**
 * RenameBranchDialog — Radix dialog for renaming a branch.
 */

import { useState, useEffect } from "react";
import { Button, Dialog, Flex, Text, TextField } from "@radix-ui/themes";

import { useProvenanceContext } from "../../context/ProvenanceContext";
import { useRenameBranch } from "../../hooks/useVersioning";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentName: string;
}

export function RenameBranchDialog({ open, onOpenChange, currentName }: Props) {
  const { projectId, currentBranch, setBranch } = useProvenanceContext();
  const renameMutation = useRenameBranch(projectId);
  const [newName, setNewName] = useState(currentName);

  useEffect(() => {
    if (open) setNewName(currentName);
  }, [open, currentName]);

  const handleSubmit = () => {
    const trimmed = newName.trim();
    if (!trimmed || trimmed === currentName) return;
    renameMutation.mutate(
      { oldName: currentName, newName: trimmed },
      {
        onSuccess: () => {
          if (currentBranch === currentName) setBranch(trimmed);
          onOpenChange(false);
        },
      },
    );
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content maxWidth="400px">
        <Dialog.Title>Rename Branch</Dialog.Title>
        <Dialog.Description size="2">
          Rename <Text weight="bold">{currentName}</Text> to a new name.
        </Dialog.Description>

        <Flex direction="column" gap="3" mt="4">
          <TextField.Root
            placeholder="New branch name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(); }}
            autoFocus
          />
          {renameMutation.isError && (
            <Text size="1" color="red">
              {renameMutation.error?.message ?? "Rename failed"}
            </Text>
          )}
        </Flex>

        <Flex gap="3" mt="4" justify="end">
          <Dialog.Close>
            <Button variant="soft" color="gray">Cancel</Button>
          </Dialog.Close>
          <Button
            onClick={handleSubmit}
            disabled={!newName.trim() || newName.trim() === currentName || renameMutation.isPending}
          >
            {renameMutation.isPending ? "Renaming..." : "Rename"}
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
